import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { Carousel } from 'dibs-carousel';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';
import { useUID } from 'dibs-uid';
import classnames from 'classnames';

import styles from './HpRespTopHeroBannerModule.scss';

export const HpRespTopHeroBannerModuleComponent = ({ componentModule, useLazyLoadImages }) => {
    const uid = `topHeroBanner-${useUID() || undefined}`;

    if (!componentModule) {
        return null;
    }

    const { items = [] } = componentModule;
    const isFullBleedImage = !!items.find(item => item.isFullBleedImage);

    const itemsPerPage = 1;

    return (
        <div
            className={classnames(styles.carouselContainer, {
                [styles.isFullBleed]: isFullBleedImage,
            })}
        >
            <HpRespModuleContainer>
                <HpRespCarouselTracking items={items} itemsPerPage={itemsPerPage}>
                    {({ handleIndexChange }) => (
                        <SharedInViewport>
                            {({ inViewport }) => (
                                <Carousel
                                    isAutoRun={inViewport && items.length > 1}
                                    dataTn="hero-items-carousel"
                                    showDots={items.length > 1}
                                    hideArrows
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index, isVisible }) => {
                                        const {
                                            cmsId,
                                            linkData,
                                            imageUrl,
                                            mobileImageUrl,
                                            cta,
                                            imageCopy,
                                            imageHeight,
                                            imageWidth,
                                            mobileImageHeight,
                                            mobileImageWidth,
                                        } = items[index];

                                        const itemId = `${uid}-${index}`;

                                        return (
                                            <>
                                                {imageCopy && (
                                                    <span id={itemId} className={styles.imageCopy}>
                                                        {imageCopy}
                                                    </span>
                                                )}
                                                <a
                                                    key={cmsId}
                                                    aria-label={cta}
                                                    aria-describedby={
                                                        imageCopy ? itemId : undefined
                                                    }
                                                    href={linkData?.path}
                                                    className={styles.bannerImgContainer}
                                                    style={{ opacity: isVisible ? '1' : '0' }}
                                                >
                                                    <HpLazyImageWrapper
                                                        useLazyLoad={useLazyLoadImages}
                                                        imageHeight={
                                                            mobileImageHeight || imageHeight
                                                        }
                                                        imageWidth={mobileImageWidth || imageWidth}
                                                    >
                                                        <ResponsiveImage
                                                            // do not hide image if there is no copy bc automated a11y testing won't
                                                            // be able to detect this issue
                                                            ariaHidden={
                                                                typeof imageCopy === 'string' &&
                                                                imageCopy !== ''
                                                            }
                                                            className={classnames(
                                                                styles.bannerImg,
                                                                {
                                                                    [styles.isFullBleed]: isFullBleedImage,
                                                                }
                                                            )}
                                                            src={imageUrl || mobileImageUrl}
                                                        />
                                                    </HpLazyImageWrapper>
                                                </a>
                                            </>
                                        );
                                    }}
                                    onIndexChange={handleIndexChange}
                                    renderDot={({ isCurrentDot } = {}) => (
                                        <SharedCarouselDot isActive={isCurrentDot} />
                                    )}
                                />
                            )}
                        </SharedInViewport>
                    )}
                </HpRespCarouselTracking>
            </HpRespModuleContainer>
        </div>
    );
};

HpRespTopHeroBannerModuleComponent.propTypes = {
    componentModule: PropTypes.object,
    useLazyLoadImages: PropTypes.bool,
    pageSetOffset: PropTypes.bool,
};

export const HpRespTopHeroBannerModule = createFragmentContainer(
    HpRespTopHeroBannerModuleComponent,
    {
        componentModule: graphql`
            fragment HpRespTopHeroBannerModule_componentModule on TopHeroBannerModule {
                title
                items {
                    cmsId
                    ... on TopHeroBannerItem @skip(if: $isMobile) {
                        imageUrl
                        imageHeight
                        imageWidth
                    }
                    ... on TopHeroBannerItem @include(if: $isMobile) {
                        mobileImageHeight
                        mobileImageWidth
                        mobileImageUrl
                    }
                    linkData {
                        path
                    }
                    imageCopy
                    cta
                    isFullBleedImage
                }
            }
        `,
    }
);
